// General tab of the Components item record (H100).
//
// Legacy: mod_parts/includes/tabs/parts_tab_general_item.inc (+ _update.inc).
// Rule 0: Blank's General tab — fields straight into <Grid container spacing={6}>
// inside the record's <Form className="mui-form"> (BlankGeneral.tsx).
//
// Field            legacy line   v6
// #                226-290       read-only (part number)
// Alt #            292-293       read-only (parts.alt)
// Name / Notes     296-299       read-only (parts.name / parts.description)
// Serial #         302-316       editable; a change that needs one asks for a reason
//                                (:378-388, parts_edit.js:88-142) and the save writes the
//                                SNoChange history row (update.inc:470-476)
// Batch #          317-376       read-only (legacy: editable only at status 4)
// Quantity         390-421       read-only (legacy: editable only at status 4)
// Status           422-517       read-only (legacy's 7 -> 16/18 is the removal
//                                lifecycle, which v6 has not ported)
// Location         520-524       read-only
// Store / Shelf    526-567       editable; shelf editable for an "other shelf" store
// Condition        570-584       editable
// Expires          585-588       editable (items.shelf_expiry)
// Item Notes       592-595       editable (items.note)
// Inspection Date  596-600       editable
// Description      603-608       editable (items.item_descrp)
// Comments         611-617       editable (items.comment)
import React, { useEffect, useMemo } from "react";
import { Grid, MenuItem } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { Select, TextField } from "formik-mui";
import { DatePicker } from "formik-mui-x-date-pickers";

import { useGlobalConfig } from "../../../hooks/useGlobalConfig";
import { usePartsConditionOptions } from "../../../hooks/usePartsConditionOptions";
import { usePartsStoreOptions } from "../../../hooks/usePartsStoreOptions";
import { serialChangeNeedsReason } from "../componentRecord";
import AtaChapterAutocomplete from "../../../ata-chapters/AtaChapterAutocomplete";
import type { AtaChapterRef } from "../../../ata-chapters/types";

export interface ComponentGeneralValues {
  part_number: string;
  part_superseded_by: string;
  part_name: string;
  part_notes: string;
  serial_number: string;
  batch_number: string;
  quantity: string;
  status_label: string;
  location: string;
  store: string;
  shelf: string;
  condition: string;
  shelf_expiry: Date | null;
  inspection_date: Date | null;
  notes: string;
  item_description: string;
  comment: string;
  sno_reason: string;
  /** The PART's ATA chapter (Aaron 2026-10-09); saved to the part, not the item. */
  ata_chapter: AtaChapterRef | null;
}

/** Legacy's prompt (parts_tab_general_item.inc:381, parts_edit.js:96). */
export const SNO_REASON_LABEL = "Please specify a reason for Serial # change";

/** Fields the form may send in PUT /v6/items/{id} — the editable half. */
export const EDITABLE_FIELDS = [
  "store",
  "shelf",
  "condition",
  "shelf_expiry",
  "inspection_date",
  "notes",
  "item_description",
  "comment",
  "serial_number",
] as const;

export function otherShelfStores(config?: { [key: string]: any } | null): string[] {
  const raw = config?.parts_other_shelf_stores ?? config?.other_shelf_stores ?? "";
  const list = Array.isArray(raw) ? raw : String(raw).split(",");
  return list.map((x) => String(x).toLowerCase().trim()).filter(Boolean);
}

/** Bonded store: legacy shows "-" and no free shelf (parts_tab_general_item.inc:526-567). */
const BondedShelfDefault: React.FC<{ locked: boolean }> = ({ locked }) => {
  const { values, setFieldValue } = useFormikContext<ComponentGeneralValues>();
  useEffect(() => {
    if (locked) return;
    if ((values.store || "").toLowerCase().trim() === "bonded" && !String(values.shelf || "").trim()) {
      setFieldValue("shelf", "-", false);
    }
  }, [locked, setFieldValue, values.shelf, values.store]);
  return null;
};

interface Props {
  config: { [key: string]: any };
  locked: boolean;
  /** The serial as loaded — the reason is asked for against this. */
  originalSerial: string;
  /** Whether the item has a part record: the ATA chapter is the part's, so none without one. */
  hasPart?: boolean;
}

const ComponentGeneral: React.FC<Props> = ({ config, locked, originalSerial, hasPart = true }) => {
  const { values, setFieldValue } = useFormikContext<ComponentGeneralValues>();
  const globalConfig = useGlobalConfig();
  const conditionOptions = usePartsConditionOptions(config) || [];
  const storeOptions = usePartsStoreOptions(config) || [];
  const otherStores = useMemo(() => otherShelfStores(config), [config]);
  const shelfEditable =
    !locked && otherStores.includes((values.store || "").toLowerCase().trim());
  const dateFormat = globalConfig?.date_format;
  const needsReason =
    !locked && serialChangeNeedsReason(originalSerial, values.serial_number, config?.na_sn_list);

  return (
    <Grid container spacing={6}>
      <BondedShelfDefault locked={locked} />
      <Grid item xs={12} md={6}>
        <Field component={TextField} name="part_number" label="#" fullWidth disabled />
        <Field component={TextField} name="part_name" label="Name" fullWidth disabled />
        <Field component={TextField} name="serial_number" label="Serial #" fullWidth disabled={locked} />
        {needsReason ? (
          <Field
            component={TextField}
            name="sno_reason"
            label={SNO_REASON_LABEL}
            multiline
            rows={3}
            fullWidth
            required
          />
        ) : null}
        <Field component={TextField} name="quantity" label="Quantity" fullWidth disabled />
        <Field component={TextField} name="location" label="Location" fullWidth disabled />
        <Field component={Select} name="condition" label="Condition" fullWidth disabled={locked}>
          {conditionOptions.map((option: any) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Field>
        <Field component={TextField} name="notes" label="Item Notes" multiline rows={3} fullWidth disabled={locked} />
        <Field
          component={TextField}
          name="item_description"
          label="Description"
          multiline
          rows={3}
          fullWidth
          disabled={locked}
        />
        <Field component={TextField} name="comment" label="Comments" multiline rows={3} fullWidth disabled={locked} />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field component={TextField} name="part_superseded_by" label="Alt #" fullWidth disabled />
        <Field component={TextField} name="part_notes" label="Notes" multiline rows={3} fullWidth disabled />
        <AtaChapterAutocomplete
          value={values.ata_chapter}
          onChange={(chapter) => setFieldValue("ata_chapter", chapter)}
          disabled={locked || !hasPart}
          helperText={
            hasPart
              ? `Saved to the part: applies to every item of P/N ${values.part_number || "this part"}`
              : "This component has no part record to hold a chapter"
          }
        />
        <Field component={TextField} name="batch_number" label="Batch #" fullWidth disabled />
        <Field component={TextField} name="status_label" label="Status" fullWidth disabled />
        <Field component={Select} name="store" label="Store" fullWidth disabled={locked}>
          {storeOptions.map((option: any) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Field>
        <Field
          component={TextField}
          name="shelf"
          label="Other Shelf"
          fullWidth
          disabled={!shelfEditable}
        />
        <Field
          component={DatePicker}
          name="shelf_expiry"
          label="Expires"
          inputFormat={dateFormat}
          disabled={locked}
          textField={{ fullWidth: true, disabled: locked }}
        />
        <Field
          component={DatePicker}
          name="inspection_date"
          label="Inspection Date"
          inputFormat={dateFormat}
          disabled={locked}
          textField={{ fullWidth: true, disabled: locked }}
        />
      </Grid>
    </Grid>
  );
};

export default ComponentGeneral;
